"use client";

import type { Dispatch, FormEvent, SetStateAction } from "react";
import { useState } from "react";
import type { LastReading } from "@/lib/last-reading";
import {
  fetchAccountOrders,
  requestAccountLink,
  type AccountOrdersResponse,
  type AccountOrder,
  type AccountMembership,
  type AccountReading,
} from "./account-api";
import { AccountChartCircle } from "./AccountChartCircle";
import { AccountDecisionJourneys } from "./AccountDecisionJourneys";
import { formatAccountAmount, formatAccountDate } from "./account-format";
import { AccountJyotishiHome } from "./AccountJyotishiHome";
import { MembershipPanel } from "./AccountMembershipPanel";
import { AccountReportDelivery } from "./AccountReportDelivery";
import { AccountRemedyPlans } from "./AccountRemedyPlans";
import { AccountReadingLibrary } from "./AccountReadingLibrary";

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--color-bg)] px-5 py-10 text-[var(--color-text-main)]">
      <div className="mx-auto max-w-3xl">{children}</div>
    </div>
  );
}

function SupportBlock({ email }: { email?: string }) {
  const subject = encodeURIComponent("Question about my reading");
  const body = encodeURIComponent(
    `Namaste Ishvaram,\n\n${email ? `My account: ${email}\n` : ""}I'd like to ask about:\n\n`
  );
  const mailto = `mailto:support@ishvaram.com?subject=${subject}&body=${body}`;
  return (
    <section className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-5 shadow-[var(--shadow-elevation-low)]">
      <h3 className="font-serif text-xl">Ishvaram support</h3>
      <p className="mt-2 text-sm leading-6 text-[var(--color-text-sub)]">
        Need help opening a purchased report, restoring account access, or checking delivery? Email
        the Ishvaram support desk. Kundali interpretation stays with your AI Jyotishi.
      </p>
      <a
        href={mailto}
        className="mt-4 inline-flex items-center rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-white"
      >
        Email support
      </a>
      <p className="mt-3 text-xs text-[var(--color-text-sub)]">
        Or write to <span className="font-semibold">support@ishvaram.com</span>
      </p>
    </section>
  );
}

export function LoginForm({
  notice,
  rememberedReading,
  onGoogle,
  onSubmitted,
}: {
  notice?: string;
  rememberedReading?: LastReading | null;
  onGoogle?: () => Promise<unknown>;
  onSubmitted: (channel: "email" | "phone") => void;
}) {
  const [channel, setChannel] = useState<"email" | "phone">("phone");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleError, setGoogleError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await requestAccountLink(contact);
    } catch {
      // Keep enumeration protection: the customer sees the same state either way.
    }
    setBusy(false);
    onSubmitted(channel);
  }

  async function handleGoogle() {
    if (!onGoogle || googleBusy) return;
    setGoogleBusy(true);
    setGoogleError("");
    try {
      await onGoogle();
    } catch {
      setGoogleError("Could not open Google sign-in. Try again.");
      setGoogleBusy(false);
    }
  }

  return (
    <Shell>
      <div className="mx-auto max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-6 shadow-[var(--shadow-elevation-low)]">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-accent)]">
          My Jyotishi
        </p>
        <h2 className="mt-2 font-serif text-3xl">Keep your readings together</h2>
        <p className="mt-3 text-sm leading-6 text-[var(--color-text-sub)]">
          Google sign-in opens only readings already verified with that account. A private WhatsApp
          or email link proves access to earlier readings and orders.
        </p>
        {notice ? (
          <p className="mt-4 rounded-md bg-[var(--color-warning-bg)] p-3 text-sm text-[var(--color-warning-text)]">
            {notice}
          </p>
        ) : null}
        {rememberedReading ? <RememberedReadingEntry reading={rememberedReading} /> : null}
        {onGoogle ? (
          <>
            <button
              id="account-google-sign-in"
              type="button"
              disabled={googleBusy}
              onClick={() => void handleGoogle()}
              className="mt-5 w-full rounded-md bg-[var(--color-accent)] px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {googleBusy ? "Opening Google..." : "Continue with Google"}
            </button>
            {googleError ? (
              <p className="mt-2 text-xs text-[var(--color-error-text)]">{googleError}</p>
            ) : null}
            <div className="my-5 flex items-center gap-3 text-xs text-[var(--color-text-faint)]">
              <span className="h-px flex-1 bg-[var(--color-border)]" />
              Open an earlier private purchase
              <span className="h-px flex-1 bg-[var(--color-border)]" />
            </div>
          </>
        ) : null}
        <div className="mt-5 grid grid-cols-2 rounded-md border border-[var(--color-border)] p-1">
          {(["phone", "email"] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={channel === option}
              onClick={() => {
                setChannel(option);
                setContact("");
              }}
              className={`rounded px-3 py-2 text-sm font-semibold ${
                channel === option
                  ? "bg-[var(--color-primary)] text-white"
                  : "text-[var(--color-text-sub)]"
              }`}
            >
              {option === "phone" ? "WhatsApp" : "Email"}
            </button>
          ))}
        </div>
        <form className="mt-6 grid gap-3" onSubmit={handleSubmit}>
          <label
            htmlFor="account-contact"
            className="text-sm font-semibold text-[var(--color-text-main)]"
          >
            {channel === "phone" ? "Kundali or checkout WhatsApp number" : "Checkout email"}
          </label>
          <input
            id="account-contact"
            data-cwv-interaction-target="account-contact"
            type={channel === "email" ? "email" : "tel"}
            inputMode={channel === "email" ? "email" : "tel"}
            autoComplete={channel === "email" ? "email" : "tel"}
            value={contact}
            onChange={(event) => setContact(event.target.value)}
            placeholder={channel === "email" ? "you@example.com" : "98765 43210"}
            required
            autoFocus
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-raised)] px-4 py-3 text-base text-[var(--color-text-main)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-light)]"
          />
          <button
            id="account-send-link"
            type="submit"
            disabled={busy || !contact}
            className="rounded-md bg-[var(--color-accent)] px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Sending..." : `Send link on ${channel === "phone" ? "WhatsApp" : "email"}`}
          </button>
        </form>
      </div>
    </Shell>
  );
}

function RememberedReadingEntry({ reading }: { reading: LastReading }) {
  const query = reading.language === "hi" ? "?lang=hi" : "";
  return (
    <div className="mt-5 rounded-lg border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] p-4">
      <p className="text-sm font-semibold text-[var(--color-text-main)]">
        This browser remembers {reading.name ? `${reading.name}'s` : "your latest"} reading
      </p>
      <p className="mt-1 text-xs leading-5 text-[var(--color-text-sub)]">
        Continue this reading without signing in. It cannot open any other private reading.
      </p>
      <a
        href={`/chat/${encodeURIComponent(reading.id)}${query}`}
        className="mt-3 inline-flex min-h-11 items-center text-sm font-semibold text-[var(--color-accent)]"
      >
        Continue latest reading
      </a>
    </div>
  );
}

function BrowserReadingEntry({
  reading,
  verifiedReadings,
}: {
  reading?: LastReading | null;
  verifiedReadings: AccountReading[];
}) {
  if (!reading || verifiedReadings.some((item) => item.reading_id === reading.id)) return null;
  return (
    <section aria-labelledby="browser-reading-heading" className="mb-6">
      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-accent)]">
        Same-browser continuity
      </p>
      <h2 id="browser-reading-heading" className="mt-1 font-serif text-2xl">
        Continue your latest reading
      </h2>
      <RememberedReadingEntry reading={reading} />
    </section>
  );
}

export function AccountSent({
  title,
  body,
  onReset,
}: {
  title: string;
  body: string;
  onReset?: () => void;
}) {
  return (
    <Shell>
      <div className="mx-auto max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-6 text-center shadow-[var(--shadow-elevation-low)]">
        <h2 className="font-serif text-3xl">{title}</h2>
        <p className="mt-3 text-sm leading-6 text-[var(--color-text-sub)]">{body}</p>
        {onReset ? (
          <button
            className="mt-6 text-sm font-semibold text-[var(--color-accent)]"
            onClick={onReset}
          >
            Use a different email or phone
          </button>
        ) : null}
      </div>
    </Shell>
  );
}

function PhysicalOrderRow({ order }: { order: AccountOrder }) {
  const amount = formatAccountAmount(order);
  return (
    <article className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-4 shadow-[var(--shadow-elevation-low)] sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="font-semibold">{order.title}</h2>
        <p className="mt-1 text-sm text-[var(--color-text-sub)]">
          {order.kind.replaceAll("_", " ")} - {formatAccountDate(order.purchased_at)}
          {amount ? ` - ${amount}` : ""}
        </p>
      </div>
      {order.track_url ? (
        <a
          className="inline-flex min-h-11 shrink-0 items-center rounded-md border border-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-accent)]"
          href={order.track_url}
        >
          Track order
        </a>
      ) : null}
    </article>
  );
}

function AccountHeader({ contact, onReset }: { contact: string; onReset: () => void }) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-accent)]">
          Private account
        </p>
        <h1 className="mt-2 font-serif text-3xl">Your Jyotishi</h1>
        <p className="mt-1 text-sm text-[var(--color-text-sub)]">Signed in as {contact}</p>
      </div>
      <button
        className="self-start text-sm font-semibold text-[var(--color-accent)]"
        onClick={onReset}
      >
        Sign out
      </button>
    </header>
  );
}

function ReportsPanel({
  reports,
  token,
  contactLabel,
}: {
  reports: AccountOrder[];
  token: string;
  contactLabel: string;
}) {
  return (
    <section aria-labelledby="reports-delivery-heading" className="mb-6">
      <div className="mb-3">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-accent)]">
          Reports and delivery
        </p>
        <h2 id="reports-delivery-heading" className="mt-1 font-serif text-2xl">
          Every report, one clear status
        </h2>
      </div>
      <div className="grid gap-4">
        {reports.length ? (
          reports.map((order) => (
            <AccountReportDelivery key={order.order_id} order={order} token={token} />
          ))
        ) : (
          <p className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-4 text-sm text-[var(--color-text-sub)]">
            No reports found for this {contactLabel} yet.
          </p>
        )}
      </div>
    </section>
  );
}

function OtherOrdersPanel({ orders }: { orders: AccountOrder[] }) {
  if (!orders.length) return null;
  return (
    <section aria-labelledby="other-orders-heading" className="mb-6">
      <h2 id="other-orders-heading" className="mb-3 font-serif text-2xl">
        Other orders
      </h2>
      <div className="grid gap-3">
        {orders.map((order) => (
          <PhysicalOrderRow key={`${order.kind}-${order.order_id}`} order={order} />
        ))}
      </div>
    </section>
  );
}

function accountContact(data: AccountOrdersResponse | null): string {
  if (data?.contact_kind === "auth") return "Verified Ishvaram account";
  return data?.contact || data?.email || data?.phone || "your checkout contact";
}

function accountContactLabel(kind?: AccountOrdersResponse["contact_kind"]): string {
  if (kind === "phone") return "WhatsApp number";
  if (kind === "auth") return "verified account";
  return "email";
}

function accountDecisionJourneys(data: AccountOrdersResponse | null) {
  return data?.decision_journeys ?? [];
}

function accountRemedyPlans(data: AccountOrdersResponse | null) {
  return data?.remedy_plans ?? [];
}

function isVerifiedAccount(data: AccountOrdersResponse | null): boolean {
  return data?.contact_kind === "auth";
}

const EMPTY_ACCOUNT_DATA: AccountOrdersResponse = {
  email: "",
  orders: [],
  membership: null,
  readings: [],
};

function accountReadyView(data: AccountOrdersResponse | null) {
  const account = data ?? EMPTY_ACCOUNT_DATA;
  const orders = account.orders;
  return {
    contact: accountContact(account),
    contactLabel: accountContactLabel(account.contact_kind),
    email: account.email,
    membership: account.membership,
    membershipEnrollmentOpen: Boolean(account.membership_enrollment_open),
    readings: account.readings,
    snapshot: account.jyotishi_snapshot ?? null,
    relationshipHome: account.relationship_home ?? null,
    chartSubjects: account.chart_subjects ?? [],
    decisionJourneys: accountDecisionJourneys(account),
    remedyPlans: accountRemedyPlans(account),
    verified: isVerifiedAccount(account),
    reports: orders.filter((order) => order.kind === "digital_report"),
    otherOrders: orders.filter((order) => order.kind !== "digital_report"),
  };
}

export function AccountReady({
  data,
  token,
  rememberedReading,
  onDataChange,
  onReset,
}: {
  data: AccountOrdersResponse | null;
  token: string;
  rememberedReading?: LastReading | null;
  onDataChange: Dispatch<SetStateAction<AccountOrdersResponse | null>>;
  onReset: () => void;
}) {
  const {
    contact,
    contactLabel,
    email,
    membership,
    membershipEnrollmentOpen,
    readings,
    snapshot,
    relationshipHome,
    chartSubjects,
    decisionJourneys,
    remedyPlans,
    verified,
    reports,
    otherOrders,
  } = accountReadyView(data);
  function handleMembershipCancelled(membership: AccountMembership) {
    onDataChange((current) => (current ? { ...current, membership } : current));
  }
  async function refreshAccount() {
    const refreshed = await fetchAccountOrders(token);
    if (refreshed === "unauthorized") throw new Error("Your account session has expired.");
    onDataChange(refreshed);
  }

  return (
    <Shell>
      <AccountHeader contact={contact} onReset={onReset} />
      <BrowserReadingEntry reading={rememberedReading} verifiedReadings={readings} />
      <AccountJyotishiHome
        readings={readings}
        snapshot={snapshot}
        home={relationshipHome}
        token={token}
      />
      <AccountChartCircle
        subjects={chartSubjects}
        readings={readings}
        token={token}
        onRefresh={refreshAccount}
      />
      <AccountDecisionJourneys
        chartSubjects={chartSubjects}
        journeys={decisionJourneys}
        readings={readings}
        token={token}
        verified={verified}
        onRefresh={refreshAccount}
      />
      <AccountRemedyPlans
        plans={remedyPlans}
        token={token}
        verified={verified}
        onRefresh={refreshAccount}
      />
      <MembershipPanel
        membership={membership}
        enrollmentOpen={membershipEnrollmentOpen}
        token={token}
        onCancelled={handleMembershipCancelled}
      />
      <AccountReadingLibrary readings={readings.slice(1)} />
      <ReportsPanel reports={reports} token={token} contactLabel={contactLabel} />
      <OtherOrdersPanel orders={otherOrders} />
      <SupportBlock email={email} />
    </Shell>
  );
}

export function AccountError({ message, onReset }: { message: string; onReset: () => void }) {
  return (
    <Shell>
      <div className="mx-auto max-w-md rounded-lg border border-[var(--color-error-tint)] bg-[var(--color-error-bg)] p-6 text-center shadow-[var(--shadow-elevation-low)]">
        <h2 className="font-serif text-3xl">Could not load account</h2>
        <p className="mt-3 text-sm leading-6 text-[var(--color-error-text-dark)]">{message}</p>
        <button className="mt-6 text-sm font-semibold text-[var(--color-accent)]" onClick={onReset}>
          Try a different email or phone
        </button>
      </div>
    </Shell>
  );
}
